import i18n from '@src/i18n'
import { ERROR_CODE_I18N_MAP } from './errorCodeMap'

export const getNestedError = (err?: unknown): string | null => {
  if (!err) {
    return null
  }
  const isArray = Array.isArray(err)

  const isString = typeof err === 'string'
  const isObject = typeof err === 'object'

  if (isArray) {
    const errorItem = err.find((errItem) => !!getNestedError(errItem))
    return getNestedError(errorItem)
  }
  if (isObject) {
    return getNestedError(Object.values(err || {}))
  }
  if (isString) {
    return err
  }

  return null
}

/**
 * Structured error contract emitted by the backend for field-attached errors.
 *
 * Source: a serializer raises ``StructuredValidationError`` (see
 * ``approveit/exceptions.py``), which produces a leaf of this shape inside
 * ``response.data.details.<field>``:
 *
 *   { code: "field.unique",
 *     message: "A vendor with this name already exists.",
 *     params: { resource: "vendor", field: "name" } }
 *
 * The FE looks up ``code`` in ERROR_CODE_I18N_MAP, interpolates ``params``,
 * and falls back to ``message`` when no translation entry exists.
 */
export interface StructuredError {
  code: string
  message: string
  params?: Record<string, unknown>
}

const isStructuredError = (data: unknown): data is StructuredError => {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return false
  const obj = data as Record<string, unknown>
  return typeof obj.code === 'string' && typeof obj.message === 'string'
}

const resolveStructuredError = (err: StructuredError): string => {
  const i18nKey = ERROR_CODE_I18N_MAP[err.code]
  if (i18nKey) {
    // i18next returns the key string itself when no translation is found in
    // the active locale (after exhausting fallback locales). Treat that as a
    // miss and use the server-side ``message`` instead, otherwise the raw
    // key (e.g. ``"common:errors.uniqueField"``) would leak to the user.
    const translated = i18n.t(i18nKey, err.params || {}) as string
    if (translated && translated !== i18nKey) {
      return translated
    }
  }
  return err.message
}

export const extractErrorMessages = (data: Record<string, unknown> | string, path = ''): string[] => {
  // Structured errors carry their own context (resource/field) inside the
  // payload, so we render them bare — no path prefix. Stops recursion here.
  // This is the canonical contract: backend raises ``StructuredValidationError``
  // → ``code`` is mapped to an i18n key on the frontend. Anything else falls
  // through to the path-prefixed walker below as legacy/unstructured.
  if (isStructuredError(data)) {
    return [resolveStructuredError(data)]
  }

  const messages: string[] = []

  if (typeof data === 'string') {
    messages.push(`${path ? path + ': ' : ''}${data}`)
  } else if (Array.isArray(data)) {
    data.forEach((item, index) => {
      messages.push(...extractErrorMessages(item, `${path}[${index}]`))
    })
  } else if (typeof data === 'object' && data !== null) {
    for (const key in data) {
      messages.push(...extractErrorMessages(data[key] as Record<string, unknown>, path ? `${path}.${key}` : key))
    }
  }

  return messages
}
